import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ApiFeil } from '../../api/klient';
import { konsollApi, type Ansatt, type ReiseDetaljer } from '../../api/konsoll';
import DokumentasjonPanel from './DokumentasjonPanel';
import FlaggPanel from './FlaggPanel';
import InvitasjonKnapp from './InvitasjonKnapp';
import OverleveringPanel from './OverleveringPanel';
import SammendragPanel from './SammendragPanel';
import SvarPanel from './SvarPanel';
import TidPanel from './TidPanel';

type Fane = 'svar' | 'flagg' | 'dokumentasjon' | 'tid' | 'sammendrag' | 'overlevering';
const FANER: Fane[] = ['svar', 'flagg', 'dokumentasjon', 'tid', 'sammendrag', 'overlevering'];

/** /konsoll/reiser/:id. Én reise: svar, flagg, tid, sammendrag og overlevering. */
export default function ReiseDetalj({ ansatt }: { ansatt: Ansatt }) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const [detaljer, setDetaljer] = useState<ReiseDetaljer | null>(null);
  const [fane, setFane] = useState<Fane>('svar');
  const [feil, setFeil] = useState<string | null>(null);

  const hent = useCallback(
    () =>
      konsollApi
        .detaljer(id)
        .then((d) => {
          setDetaljer(d);
          setFeil(null);
        })
        .catch((e) => setFeil(e instanceof ApiFeil ? e.kode : 'ukjent')),
    [id]
  );

  useEffect(() => {
    hent();
  }, [hent]);

  return (
    <div>
      <Link to=".." relative="path" className="mb-4 inline-block text-sm underline">
        ← {t('konsoll.detalj.tilbake')}
      </Link>

      {feil && (
        <p role="alert" className="mb-4 melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}
      {!detaljer && !feil && <p role="status">{t('konsoll.laster')}</p>}

      {detaljer && (
        <>
          <header className="mb-4">
            <h2 className="mb-1 text-[1.625rem] font-semibold">{detaljer.reise.kundenavn}</h2>
            <p className="text-sm text-slate-700">
              <span className="merke mr-2">{t(`konsoll.tilstand.${detaljer.reise.tilstand}`)}</span>
              {t('konsoll.detalj.besvart', { besvart: detaljer.fremdrift.besvart, synlige: detaljer.fremdrift.synlige })} ·{' '}
              {t('konsoll.detalj.rettelser', { antall: detaljer.rettelser })}
            </p>
            <p className="mt-1 text-sm text-slate-700">
              {t('konsoll.detalj.deltakere')}:{' '}
              {detaljer.deltakere
                .map((d) => `${d.navn} (${d.erEier ? t('konsoll.detalj.eier') : d.moduler.length ? d.moduler.join(', ') : t('konsoll.detalj.alleModuler')})`)
                .join(', ')}
            </p>
            <InvitasjonKnapp
              reiseId={id}
              sendt={detaljer.deltakere.find((d) => d.erEier)?.invitasjonSendt}
              stengt={detaljer.reise.tilstand === 'trukket_tilbake'}
              paminnelserAv={detaljer.reise.paminnelserAv ?? false}
              onSendt={hent}
            />
          </header>

          <nav aria-label={detaljer.reise.kundenavn} className="mb-6 flex flex-wrap gap-x-7 border-b border-slate-200">
            {FANER.map((f) => (
              <button
                key={f}
                type="button"
                aria-current={fane === f ? 'page' : undefined}
                onClick={() => setFane(f)}
                className={'fane -mb-px ' + (fane === f ? 'fane-aktiv' : '')}
              >
                {t(`konsoll.detalj.faner.${f}`)}
                {f === 'flagg' && detaljer.flagg.length > 0 ? ` (${detaljer.flagg.filter((x) => x.status !== 'trukket').length})` : ''}
              </button>
            ))}
          </nav>

          {fane === 'svar' && <SvarPanel reiseId={id} detaljer={detaljer} onEndret={hent} />}
          {fane === 'flagg' && <FlaggPanel reiseId={id} flagg={detaljer.flagg} />}
          {fane === 'tid' && <TidPanel reiseId={id} tid={detaljer.tid} ansatt={ansatt} onEndret={hent} />}
          {fane === 'sammendrag' && <SammendragPanel reiseId={id} tilstand={detaljer.reise.tilstand} onEndret={hent} />}
          {fane === 'dokumentasjon' && <DokumentasjonPanel reiseId={id} />}
          {fane === 'overlevering' && <OverleveringPanel reiseId={id} />}
        </>
      )}
    </div>
  );
}
